import type {Thread, Comment} from '@github-ui/diff-lines/conversation-types'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {
  useDiffSummariesActions,
  useMarkersActions,
  usePendingReviewActions,
  usePullRequestBasePath,
  useUnresolvedConversationCountActions,
} from '../stores/selectors'
import {threadPreviewsQueryKey} from '../page-data/loaders/use-thread-previews'
import {
  CommentSubjectType,
  type FileCommentPosition,
  type FileKey,
  type LineCommentPosition,
  type LineKey,
  type MultilineCommentPosition,
} from '@github-ui/diff-lines/types'
import {buildSubjectFromCommentData} from '../helpers/diff-helpers'

export type Side = 'left' | 'right'
export type CreateReviewCommentMutationData = {
  text: string
  line?: number
  path: string
  positioning?: LineCommentPosition | MultilineCommentPosition | FileCommentPosition
  side?: Side
  startLine?: number
  startSide?: Side
  subjectType?: CommentSubjectType
  submitBatch?: boolean
  comparisonStartOid?: string | null | undefined
  comparisonEndOid?: string | null | undefined
}

type MutationResponse = {
  thread: Thread
  comment: Comment
  totalCommentsCount: number
}

export function useCreateReviewCommentMutation() {
  const basePath = usePullRequestBasePath()
  const queryClient = useQueryClient()
  const apiUrl = `${basePath}/page_data/${PageData.createReviewComment}`
  const {updateThread} = useMarkersActions()
  const {onCommentThreadAdded} = useDiffSummariesActions()
  const {addPendingComment, clearPendingReview} = usePendingReviewActions()
  const {incrementUnresolvedConversationCount} = useUnresolvedConversationCountActions()

  return useMutation({
    mutationFn: async (data: CreateReviewCommentMutationData) => {
      const result = await reactFetchJSON(`${apiUrl}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: data,
      })

      const json = await parseJSONWithBetterErrors(result)
      throwErrorsIfBadResponse(result, json)
      return json
    },
    onSuccess: (data: MutationResponse, inputs: CreateReviewCommentMutationData) => {
      // update the MarkersMap for the TOC store
      const diffLineKey =
        inputs.subjectType === CommentSubjectType.File
          ? ('FILE' as FileKey)
          : ((inputs.side === 'right' ? `R${inputs.line}` : `L${inputs.line}`) as LineKey)

      const startPosition =
        inputs.startSide && inputs.startLine
          ? inputs.startSide === 'right'
            ? `R${inputs.startLine}`
            : `L${inputs.startLine}`
          : undefined

      if (inputs.submitBatch !== undefined && !inputs.submitBatch && data.thread) {
        addPendingComment(Number(data.thread.id))
      }

      // Clear the pending review IDs if the comment is submitted as part of a batch
      if (inputs.submitBatch) {
        clearPendingReview()
      }

      // update the Markers Store
      updateThread(Number(data.thread.id), inputs.path, diffLineKey, () => {
        const subject = buildSubjectFromCommentData(inputs)
        data.thread.subject = subject
        data.thread.positioning = subject
        return data.thread
      })
      onCommentThreadAdded({
        path: inputs.path,
        diffLineKey,
        startPosition,
        threadID: data.thread.id,
      })

      incrementUnresolvedConversationCount()

      // invalidate pullRequestFilesToolbar queries
      queryClient.invalidateQueries({
        queryKey: threadPreviewsQueryKey(basePath),
      })
    },
  })
}
